import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Coins,
  Globe,
  Users,
  Workflow,
  Mail,
  ExternalLink,
  Shield,
  Phone,
  Contact,
  Activity,
  MessageCircle,
  Send,
  Share2,
  Bot,
  FileText,
} from 'lucide-react';
import { fastapiService } from '../../services/fastapiService';
import { ENTERPRISE_ADMIN_BASE } from '../../config/adminLayers';
import { WorkspaceCallLogsModal } from '../../components/workspaces/WorkspaceCallLogsModal';
import { WorkspaceVoicePlatformCard } from '../../components/workspaces/WorkspaceVoicePlatformCard';

type OverviewData = {
  workspace: Record<string, unknown> & { id?: string; name?: string; owner_id?: string; is_active?: boolean; created_at?: string };
  owner: { id?: string; email?: string; full_name?: string; role?: string } | null;
  member_count: number;
  credit_account: Record<string, unknown> | null;
  verified_domains: Array<Record<string, unknown>>;
  verified_domain_count: number;
  workflow_count: number;
  whitelist: { enabled?: boolean; custom_domain?: string; cname_verified?: boolean; company_name?: string } | null;
  /** Usage stats (all-time row counts where applicable) */
  contact_count?: number;
  call_count?: number;
  workflow_execution_count?: number;
  whatsapp_message_count?: number;
  outbound_message_count?: number;
  social_message_count?: number;
  ai_conversation_count?: number;
};

function formatInvoiceCurrency(amount: number, currency = 'INR') {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(amount);
}

function invoiceStatusClass(status: string) {
  switch (status?.toLowerCase()) {
    case 'paid':
      return 'bg-emerald-100 text-emerald-800';
    case 'pending':
      return 'bg-amber-100 text-amber-800';
    case 'overdue':
      return 'bg-red-100 text-red-800';
    case 'cancelled':
      return 'bg-enterprise-100 text-enterprise-700';
    default:
      return 'bg-enterprise-100 text-enterprise-700';
  }
}

/**
 * Enterprise workspace 360° — uses GET /admin/workspaces/:id/overview (additive API).
 */
type AdminInvoiceRow = {
  id: string;
  invoice_number?: string;
  amount?: string | number;
  total_amount?: string | number;
  currency?: string;
  status?: string;
  created_at?: string;
  user_profiles?: { email?: string; full_name?: string };
};

export const EnterpriseWorkspaceDetailPage: React.FC = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<OverviewData | null>(null);
  const [invoices, setInvoices] = useState<AdminInvoiceRow[]>([]);
  const [invoicesTotal, setInvoicesTotal] = useState(0);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState('');
  const [callLogsOpen, setCallLogsOpen] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await fastapiService.getWorkspaceAdminOverview(workspaceId);
        if (cancelled) return;
        const d = res.data as unknown as OverviewData;
        if (!d?.workspace) {
          setError('Invalid response');
          return;
        }
        setData(d);
      } catch (e: unknown) {
        if (!cancelled) {
          const err = e as { error?: string; message?: string };
          setError(err?.error || err?.message || 'Failed to load workspace');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    const loadInv = async () => {
      try {
        setInvoicesLoading(true);
        setInvoicesError('');
        const res = await fastapiService.getInvoices({
          workspace_id: workspaceId,
          page: 1,
          limit: 50,
        });
        if (cancelled) return;
        setInvoices((res.data || []) as AdminInvoiceRow[]);
        setInvoicesTotal(res.pagination?.total ?? 0);
      } catch (e: unknown) {
        if (!cancelled) {
          const err = e as { error?: string; message?: string };
          setInvoicesError(err?.error || err?.message || 'Failed to load invoices');
          setInvoices([]);
          setInvoicesTotal(0);
        }
      } finally {
        if (!cancelled) setInvoicesLoading(false);
      }
    };
    loadInv();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  if (!workspaceId) {
    return <p className="text-enterprise-600">Missing workspace id.</p>;
  }

  const ws = data?.workspace;
  const name = ws?.name || 'Workspace';

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to={`${ENTERPRISE_ADMIN_BASE}/workspaces`}
          className="inline-flex items-center gap-1 text-sm font-medium text-enterprise-600 hover:text-emerald-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Directory
        </Link>
        <span className="text-enterprise-300">/</span>
        <span className="text-sm text-enterprise-500 truncate max-w-[200px] sm:max-w-md">{name}</span>
      </div>

      {loading && <p className="py-12 text-center text-enterprise-500">Loading overview…</p>}
      {error && !loading && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {!loading && data && ws && (
        <>
          <div className="rounded-2xl border border-enterprise-200 bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100 text-emerald-800">
                  <Building2 className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-enterprise-900">{name}</h2>
                  <p className="mt-1 font-mono text-xs text-enterprise-500">{String(ws.id)}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        ws.is_active !== false ? 'bg-emerald-100 text-emerald-800' : 'bg-enterprise-100 text-enterprise-700'
                      }`}
                    >
                      {ws.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                    {ws.created_at && (
                      <span className="text-xs text-enterprise-500">
                        Created {new Date(String(ws.created_at)).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link
                  to={`${ENTERPRISE_ADMIN_BASE}/workspaces/table`}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-enterprise-200 bg-enterprise-50 px-3 py-2 text-xs font-medium text-enterprise-800 hover:bg-enterprise-100"
                >
                  Workspaces table
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-enterprise-500">Workspace &amp; billing</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 text-enterprise-500">
                  <Users className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-wide">Members</span>
                </div>
                <p className="mt-2 text-2xl font-bold text-enterprise-900">{data.member_count}</p>
              </div>
              <div className="rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 text-enterprise-500">
                  <Workflow className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-wide">Workflows</span>
                </div>
                <p className="mt-2 text-2xl font-bold text-enterprise-900">{data.workflow_count}</p>
              </div>
              <div className="rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 text-enterprise-500">
                  <Globe className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-wide">Verified domains</span>
                </div>
                <p className="mt-2 text-2xl font-bold text-enterprise-900">{data.verified_domain_count}</p>
              </div>
              <div className="rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 text-enterprise-500">
                  <Coins className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-wide">Credit balance</span>
                </div>
                <p className="mt-2 text-2xl font-bold text-enterprise-900">
                  {data.credit_account != null
                    ? String((data.credit_account as { balance?: unknown }).balance ?? '—')
                    : '—'}
                </p>
              </div>
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-enterprise-500">Activity (all-time counts)</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 text-enterprise-500">
                  <Contact className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-wide">Contacts</span>
                </div>
                <p className="mt-2 text-2xl font-bold text-enterprise-900">{data.contact_count ?? '—'}</p>
              </div>
              <button
                type="button"
                onClick={() => setCallLogsOpen(true)}
                className="rounded-xl border border-enterprise-200 bg-white p-4 text-left shadow-sm transition-all hover:border-emerald-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40"
              >
                <div className="flex items-center gap-2 text-enterprise-500">
                  <Phone className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-wide">Calls</span>
                </div>
                <p className="mt-2 text-2xl font-bold text-enterprise-900">{data.call_count ?? '—'}</p>
                <p className="mt-1 text-[10px] text-enterprise-400">Click to view call logs &amp; recordings</p>
              </button>
              <div className="rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 text-enterprise-500">
                  <Activity className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-wide">Workflow runs</span>
                </div>
                <p className="mt-2 text-2xl font-bold text-enterprise-900">{data.workflow_execution_count ?? '—'}</p>
              </div>
              <div className="rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 text-enterprise-500">
                  <MessageCircle className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-wide">WhatsApp msgs</span>
                </div>
                <p className="mt-2 text-2xl font-bold text-enterprise-900">{data.whatsapp_message_count ?? '—'}</p>
              </div>
              <div className="rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 text-enterprise-500">
                  <Send className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-wide">Outbound queue</span>
                </div>
                <p className="mt-2 text-2xl font-bold text-enterprise-900">{data.outbound_message_count ?? '—'}</p>
                <p className="mt-1 text-[10px] text-enterprise-400">Queued sends (SMS / WhatsApp / email / voice)</p>
              </div>
              <div className="rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 text-enterprise-500">
                  <Share2 className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-wide">Social inbox</span>
                </div>
                <p className="mt-2 text-2xl font-bold text-enterprise-900">{data.social_message_count ?? '—'}</p>
              </div>
              <div className="rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 text-enterprise-500">
                  <Bot className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-wide">AI Assist threads</span>
                </div>
                <p className="mt-2 text-2xl font-bold text-enterprise-900">{data.ai_conversation_count ?? '—'}</p>
              </div>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-enterprise-200 bg-white p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-enterprise-900">Owner</h3>
              {data.owner ? (
                <div className="mt-3 space-y-1 text-sm">
                  <p className="font-medium text-enterprise-800">{data.owner.full_name || data.owner.email}</p>
                  <p className="text-enterprise-600">{data.owner.email}</p>
                  {data.owner.id && (
                    <Link
                      to={`${ENTERPRISE_ADMIN_BASE}/users/${data.owner.id}`}
                      className="inline-flex items-center gap-1 text-emerald-700 hover:underline"
                    >
                      Open user profile
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  )}
                </div>
              ) : (
                <p className="mt-3 text-sm text-enterprise-500">No owner loaded.</p>
              )}
            </div>

            <div className="rounded-xl border border-enterprise-200 bg-white p-5 shadow-sm">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-enterprise-900">
                <Shield className="h-4 w-4 text-enterprise-500" />
                Whitelabel snapshot
              </h3>
              {data.whitelist ? (
                <ul className="mt-3 space-y-1 text-sm text-enterprise-700">
                  <li>Enabled: {data.whitelist.enabled ? 'Yes' : 'No'}</li>
                  <li>Domain: {data.whitelist.custom_domain || '—'}</li>
                  <li>CNAME verified: {data.whitelist.cname_verified ? 'Yes' : 'No'}</li>
                  <li>Company: {data.whitelist.company_name || '—'}</li>
                </ul>
              ) : (
                <p className="mt-3 text-sm text-enterprise-500">No whitelist data in workspace settings.</p>
              )}
            </div>

            {workspaceId && (
              <WorkspaceVoicePlatformCard
                workspaceId={workspaceId}
                workspaceName={data.workspace.name as string | undefined}
              />
            )}
          </div>

          <div className="rounded-xl border border-enterprise-200 bg-white p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-enterprise-900">Sending domains</h3>
            {data.verified_domains.length === 0 ? (
              <p className="mt-2 text-sm text-enterprise-500">No verified_domains rows for this workspace.</p>
            ) : (
              <ul className="mt-3 divide-y divide-enterprise-100">
                {data.verified_domains.map((d) => (
                  <li key={String(d.id)} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                    <span className="font-medium text-enterprise-800">{String(d.domain_name)}</span>
                    <span className="text-enterprise-500">
                      {String(d.provider_type || '')} · {String(d.verification_status || '')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-xl border border-enterprise-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-enterprise-900">
                <FileText className="h-4 w-4 text-enterprise-500" />
                Invoices
              </h3>
              <Link
                to={`${ENTERPRISE_ADMIN_BASE}/invoices?workspace_id=${encodeURIComponent(workspaceId)}`}
                className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline"
              >
                Open full list &amp; pagination
                <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
            <p className="mt-1 text-xs text-enterprise-500">
              Showing up to 50 · {invoicesTotal} total for this workspace
            </p>
            {invoicesError ? (
              <p className="mt-3 text-sm text-red-600">{invoicesError}</p>
            ) : invoicesLoading ? (
              <p className="mt-4 text-sm text-enterprise-500">Loading invoices…</p>
            ) : invoices.length === 0 ? (
              <p className="mt-4 text-sm text-enterprise-500">No invoices for this workspace.</p>
            ) : (
              <div className="mt-4 overflow-x-auto rounded-lg border border-enterprise-100">
                <table className="min-w-full divide-y divide-enterprise-100 text-sm">
                  <thead className="bg-enterprise-50/80">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-enterprise-500">
                        Invoice #
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-enterprise-500">
                        Customer
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-enterprise-500">
                        Amount
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-enterprise-500">
                        Status
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-enterprise-500">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-enterprise-100 bg-white">
                    {invoices.map((inv) => (
                      <tr key={inv.id} className="hover:bg-enterprise-50/50">
                        <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-enterprise-900">
                          {inv.invoice_number || `INV-${inv.id.substring(0, 8).toUpperCase()}`}
                        </td>
                        <td className="px-3 py-2 text-enterprise-800">
                          <div>{inv.user_profiles?.email || '—'}</div>
                          {inv.user_profiles?.full_name ? (
                            <div className="text-xs text-enterprise-500">{inv.user_profiles.full_name}</div>
                          ) : null}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 font-medium text-enterprise-900">
                          {formatInvoiceCurrency(
                            parseFloat(String(inv.amount ?? inv.total_amount ?? 0)) || 0,
                            inv.currency || 'INR',
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${invoiceStatusClass(
                              inv.status || '',
                            )}`}
                          >
                            {inv.status || '—'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-enterprise-600">
                          {inv.created_at ? new Date(inv.created_at).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-dashed border-enterprise-300 bg-enterprise-50/50 p-4">
            <h3 className="text-sm font-semibold text-enterprise-900">Classic actions</h3>
            <p className="mt-1 text-xs text-enterprise-600">
              Use the classic workspaces list for add credits &amp; email provider until those flows are migrated here.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to={`${ENTERPRISE_ADMIN_BASE}/workspaces/table`}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700"
              >
                <Coins className="h-3.5 w-3.5" />
                Credits &amp; email (table)
              </Link>
              {data.owner?.id && (
                <Link
                  to={`${ENTERPRISE_ADMIN_BASE}/users/${data.owner.id}`}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-enterprise-300 px-3 py-2 text-xs font-medium text-enterprise-800 hover:bg-white"
                >
                  <Mail className="h-3.5 w-3.5" />
                  User billing
                </Link>
              )}
            </div>
          </div>
        </>
      )}

      {callLogsOpen ? (
        <WorkspaceCallLogsModal
          workspaceId={workspaceId}
          workspaceName={name}
          isOpen
          onClose={() => setCallLogsOpen(false)}
        />
      ) : null}
    </div>
  );
};
